import type {
  RuntimeActionDispatchResponse,
  RuntimeActionExecuteResponse,
  RuntimeDataQueryRequest,
  RuntimeDataQueryResponse,
  RuntimeNavigationGraph,
  RuntimeScreenResponse,
  RuntimeSurface,
} from '@boox/contracts';

import { api, type ApiQueryParams } from '@/lib/api';
import {
  browserRouteForDynamicRuntimeRoute,
  dynamicRuntimeBindingForCategory,
  dynamicRuntimeBindingForPath,
  isPathInDynamicRuntimeBinding,
  titleForDynamicRuntimePath,
  type DynamicRuntimeBinding,
} from '@/lib/dynamic-runtime-bindings';

type RuntimeModuleRegistryQuery = Partial<RuntimeModuleRegistryRequest>;

export type RuntimeModuleType = 'horizontal' | 'vertical' | 'profile';
export type RuntimeSurfaceKey = 'web' | 'pwa' | 'extension' | 'self-service' | 'admin' | 'api' | 'share';
export type RuntimeSettingsScope = 'platform' | 'tenant' | 'module' | 'profile' | 'user';

export type RuntimeModuleRegistryRecord = {
  moduleCode: string;
  moduleId: string;
  moduleType: RuntimeModuleType;
  ownerLayer: 'platform' | 'horizontal' | 'vertical';
  routeBase: string;
  routeAliases: string[];
  defaultScreenKey: string;
  defaultProfileId?: string;
  parentModuleCode?: string;
  capabilityRefs: {
    dataQuery: string[];
    action: string[];
  };
  supportedSurfaces: RuntimeSurfaceKey[];
  requiredPermissions: string[];
  enablement: {
    enabled: boolean;
    source: 'baseline' | 'tenant-config' | 'profile-override';
    reason?: string;
  };
  navigationGroup: string;
  display: {
    label: string;
    icon: string;
    order: number;
    badgeQueryRef?: string;
  };
  runtimeBinding?: {
    categoryId: string;
    categoryLabel?: string;
    categoryIcon?: string;
    categoryZone?: DynamicRuntimeBinding['categoryZone'];
    categoryOrder?: number;
    requiredPermission?: string;
    requiredModule?: string;
    defaultRoute: string;
    routePrefixes: string[];
    pwaRoutePrefixes?: string[];
    portalRoutePrefixes?: string[];
    aliases?: DynamicRuntimeBinding['aliases'];
    chatContext?: DynamicRuntimeBinding['chatContext'];
  };
};

export type RuntimeModuleRegistryRequest = {
  surface?: RuntimeSurface;
  locale?: string;
  includeDisabled?: boolean;
  moduleType?: RuntimeModuleType;
};

export type RuntimeModuleRegistryResponse = {
  apiBoundaryVersion: string;
  sourceOfTruth: string;
  modules: RuntimeModuleRegistryRecord[];
  profiles: Record<string, unknown>[];
  modulePacks: Record<string, unknown>[];
  foundationContracts: Record<string, unknown>[];
};

export type RuntimeResolveRouteRequest = {
  pathname: string;
  surface?: RuntimeSurface;
  locale?: string;
  profileTemplateCode?: string;
  entityId?: string;
  shareContextRef?: string;
};

export type RuntimeResolvedRoute = {
  moduleCode: string;
  profileCode?: string;
  screenKey: string;
  screenType: 'overview' | 'entity-list' | 'workbench' | 'report' | 'setup' | 'automation';
  routeBase: string;
  canonicalPath: string;
  matchedAlias?: string;
  context: {
    recordId?: string;
    tabKey?: string;
    savedViewKey?: string;
    filters?: Record<string, string | string[]>;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    missing?: string[];
  };
};

export type RuntimeSettingsField = {
  key: string;
  label: string;
  dataType: 'string' | 'text' | 'number' | 'boolean' | 'select' | 'multi-select' | 'secret-ref' | 'json';
  required: boolean;
  defaultValue?: unknown;
  enumOptions?: Array<{ value: string; label: string }>;
  validation?: Record<string, unknown>;
  secret?: {
    maskedValue?: string;
    isConfigured: boolean;
    secretRef?: string;
  };
  explain?: {
    impact?: string[];
    riskLevel?: 'low' | 'medium' | 'high';
  };
};

export type RuntimeSettingsSection = {
  sectionKey: string;
  title: string;
  description?: string;
  permissions: string[];
  fields: RuntimeSettingsField[];
};

export type RuntimeSettingsSchemaRequest = {
  scope: RuntimeSettingsScope;
  module?: string;
  profileCode?: string;
  locale?: string;
};

export type RuntimeSettingsSchemaResponse = {
  scope: RuntimeSettingsScope;
  moduleCode?: string;
  profileCode?: string;
  version: string;
  sections: RuntimeSettingsSection[];
};

export type RuntimeSettingsDataResponse = {
  scope: RuntimeSettingsScope;
  moduleCode?: string;
  profileCode?: string;
  version: string;
  values: Record<string, unknown>;
  sourceSummary: Array<{
    layer: RuntimeSettingsScope;
    source: string;
  }>;
};

export type RuntimeActionOutcomeResponse = {
  auditId: string;
};

type RuntimeRegistryCacheEntry = {
  key: string;
  timestamp: number;
  data: RuntimeModuleRegistryResponse;
};

const RUNTIME_REGISTRY_TTL_MS = 60_000;
let runtimeRegistryCache: RuntimeRegistryCacheEntry | null = null;
let runtimeRegistryInflight: Promise<RuntimeModuleRegistryResponse> | null = null;

export function clearRuntimeClientCache(): void {
  runtimeRegistryCache = null;
  runtimeRegistryInflight = null;
}

function queryKey(query: Record<string, unknown>): string {
  return JSON.stringify(
    Object.entries(query)
      .filter(([, value]) => value !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

async function tryGet<T>(canonicalPath: string, compatibilityPath: string, query?: ApiQueryParams): Promise<T> {
  try {
    return await api.get<T>(canonicalPath, query);
  } catch {
    return api.get<T>(compatibilityPath, query);
  }
}

async function tryPost<T>(canonicalPath: string, compatibilityPath: string, body: unknown): Promise<T> {
  try {
    return await api.post<T>(canonicalPath, body);
  } catch {
    return api.post<T>(compatibilityPath, body);
  }
}

function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  const trimmed = withoutQuery.replace(/\/+$/, '');
  return trimmed || '/';
}

function longestMatchingPrefix(pathname: string, candidates: string[]): string | undefined {
  const normalized = normalizePath(pathname);
  return candidates
    .map((candidate) => normalizePath(candidate))
    .filter((candidate) => normalized === candidate || normalized.startsWith(`${candidate}/`))
    .sort((a, b) => b.length - a.length)[0];
}

function runtimeCategoryZone(moduleCode: string): DynamicRuntimeBinding['categoryZone'] {
  switch (moduleCode.toUpperCase()) {
    case 'WM':
      return 'verticals';
    case 'FM':
    case 'SC':
    case 'CF':
    case 'CT':
    case 'TM':
      return 'verticals';
    case 'ACC':
    case 'AC':
      return 'support';
    default:
      return 'verticals';
  }
}

export function dynamicRuntimeBindingFromRegistryRecord(record: RuntimeModuleRegistryRecord): DynamicRuntimeBinding {
  const runtimeBinding = record.runtimeBinding;
  if (runtimeBinding) {
    return {
      categoryId: runtimeBinding.categoryId,
      categoryLabel: runtimeBinding.categoryLabel ?? record.display.label,
      categoryIcon: runtimeBinding.categoryIcon ?? record.display.icon,
      categoryZone: runtimeBinding.categoryZone ?? runtimeCategoryZone(record.moduleCode),
      categoryOrder: runtimeBinding.categoryOrder ?? record.display.order,
      requiredPermission: runtimeBinding.requiredPermission ?? record.requiredPermissions[0],
      requiredModule: runtimeBinding.requiredModule ?? record.moduleCode,
      module: record.moduleId,
      profileTemplateCode: record.defaultProfileId,
      defaultRoute: runtimeBinding.defaultRoute,
      routePrefixes: [
        runtimeBinding.defaultRoute,
        ...(runtimeBinding.routePrefixes ?? []),
      ]
        .map((route) => normalizePath(route))
        .filter((route, index, items) => items.indexOf(route) === index),
      pwaRoutePrefixes: runtimeBinding.pwaRoutePrefixes,
      portalRoutePrefixes: runtimeBinding.portalRoutePrefixes,
      aliases: runtimeBinding.aliases,
      chatContext: runtimeBinding.chatContext,
    };
  }
  return {
    categoryId: record.moduleCode.toLowerCase(),
    categoryLabel: record.display.label,
    categoryIcon: record.display.icon,
    categoryZone: runtimeCategoryZone(record.moduleCode),
    categoryOrder: record.display.order,
    requiredPermission: record.requiredPermissions[0],
    requiredModule: record.moduleCode,
    module: record.moduleId,
    profileTemplateCode: record.defaultProfileId,
    defaultRoute: record.routeBase,
    routePrefixes: [record.routeBase, ...record.routeAliases]
      .map((route) => normalizePath(route))
      .filter((route, index, items) => items.indexOf(route) === index),
  };
}

function mergeBindingWithFallback(
  binding: DynamicRuntimeBinding,
  fallback: DynamicRuntimeBinding | undefined,
): DynamicRuntimeBinding {
  if (!fallback) return binding;
  const preferFallbackProfile = binding.categoryId === 'wm'
    && fallback.profileTemplateCode === 'wm-yard-bundle-profile';
  const preferFallbackCategoryLabel = binding.categoryId === 'sc'
    && typeof fallback.categoryLabel === 'string'
    && fallback.categoryLabel.length > 0;
  const aliases = [
    ...(binding.aliases ?? []),
    ...(fallback.aliases ?? []),
  ].filter((alias, index, items) => (
    items.findIndex((candidate) => candidate.browserRoute === alias.browserRoute) === index
  ));
  return {
    ...fallback,
    ...binding,
    categoryLabel: preferFallbackCategoryLabel
      ? fallback.categoryLabel
      : binding.categoryLabel ?? fallback.categoryLabel,
    // Preserve the tenant-resolved SC profile from the runtime registry.
    // If we blank it here, `/sc/*` routes fall back to the generic safe screen
    // instead of the correct quotation/order desk profile.
    profileTemplateCode: preferFallbackProfile
      ? fallback.profileTemplateCode
      : binding.profileTemplateCode ?? fallback.profileTemplateCode,
    aliases: aliases.length > 0 ? aliases : undefined,
    chatContext: binding.chatContext ?? fallback.chatContext,
    pwaRoutePrefixes: binding.pwaRoutePrefixes ?? fallback.pwaRoutePrefixes,
    portalRoutePrefixes: binding.portalRoutePrefixes ?? fallback.portalRoutePrefixes,
    routePrefixes: [
      ...binding.routePrefixes,
      ...fallback.routePrefixes,
    ].filter((route, index, items) => route.length > 0 && items.indexOf(route) === index),
  };
}

function bindingFromRegistryPath(
  pathname: string,
  registry: RuntimeModuleRegistryResponse | null,
): DynamicRuntimeBinding | undefined {
  if (!registry) return undefined;
  const normalized = normalizePath(pathname);
  const matched = registry.modules
    .map((record: RuntimeModuleRegistryRecord) => ({
      record,
      prefix: longestMatchingPrefix(normalized, [record.routeBase, ...record.routeAliases]),
    }))
    .filter((item: { record: RuntimeModuleRegistryRecord; prefix: string | undefined }): item is { record: RuntimeModuleRegistryRecord; prefix: string } => Boolean(item.prefix))
    .sort((a: { prefix: string }, b: { prefix: string }) => b.prefix.length - a.prefix.length)[0];
  if (!matched) return undefined;
  const registryBinding = dynamicRuntimeBindingFromRegistryRecord(matched.record);
  const fallback = dynamicRuntimeBindingForCategory(registryBinding.categoryId);
  return mergeBindingWithFallback(registryBinding, fallback);
}

function bindingFromRegistryCategory(
  categoryId: string | undefined,
  registry: RuntimeModuleRegistryResponse | null,
): DynamicRuntimeBinding | undefined {
  if (!categoryId || !registry) return undefined;
  const normalizedCategoryId = categoryId.trim().toLowerCase();
  const record = registry.modules.find((module: RuntimeModuleRegistryRecord) => (
    module.moduleCode.toLowerCase() === normalizedCategoryId
    || module.moduleId.toLowerCase() === normalizedCategoryId
    || module.runtimeBinding?.categoryId.toLowerCase() === normalizedCategoryId
  ));
  if (!record) return undefined;
  return mergeBindingWithFallback(
    dynamicRuntimeBindingFromRegistryRecord(record),
    dynamicRuntimeBindingForCategory(normalizedCategoryId),
  );
}

export function getCachedRuntimeModuleRegistry(): RuntimeModuleRegistryResponse | null {
  if (!runtimeRegistryCache) return null;
  if (Date.now() - runtimeRegistryCache.timestamp >= RUNTIME_REGISTRY_TTL_MS) return null;
  return runtimeRegistryCache.data;
}

function setRuntimeRegistryCache(key: string, data: RuntimeModuleRegistryResponse): RuntimeModuleRegistryResponse {
  runtimeRegistryCache = {
    key,
    timestamp: Date.now(),
    data,
  };
  return data;
}

export async function getRuntimeModuleRegistry(
  query: RuntimeModuleRegistryQuery = {},
): Promise<RuntimeModuleRegistryResponse> {
  const normalizedQuery: RuntimeModuleRegistryRequest = {
    surface: (query.surface as RuntimeSurface | undefined) ?? 'web',
    locale: query.locale,
    includeDisabled: query.includeDisabled,
    moduleType: query.moduleType,
  };
  const key = queryKey(normalizedQuery);
  if (
    runtimeRegistryCache
    && runtimeRegistryCache.key === key
    && Date.now() - runtimeRegistryCache.timestamp < RUNTIME_REGISTRY_TTL_MS
  ) {
    return runtimeRegistryCache.data;
  }
  if (runtimeRegistryInflight) return runtimeRegistryInflight;
  runtimeRegistryInflight = tryGet<RuntimeModuleRegistryResponse>(
    '/api/runtime/module-registry',
    '/dynamic-runtime/module/profiles',
    normalizedQuery,
  ).then((result) => {
    if ('modules' in result) return setRuntimeRegistryCache(key, result);
    return setRuntimeRegistryCache(key, {
      apiBoundaryVersion: 'legacy',
      sourceOfTruth: 'dynamic-runtime/module-profiles',
      modules: [],
      profiles: [],
      modulePacks: [],
      foundationContracts: [],
    });
  }).finally(() => {
    runtimeRegistryInflight = null;
  });
  return runtimeRegistryInflight;
}

export async function primeRuntimeModuleRegistry(
  query: RuntimeModuleRegistryQuery = {},
): Promise<RuntimeModuleRegistryResponse | null> {
  try {
    return await getRuntimeModuleRegistry(query);
  } catch {
    return null;
  }
}

export function resolvedDynamicRuntimeBindingForPath(pathname: string): DynamicRuntimeBinding | undefined {
  const staticBinding = dynamicRuntimeBindingForPath(pathname);
  const registryBinding = bindingFromRegistryPath(pathname, getCachedRuntimeModuleRegistry());
  if (!registryBinding) return staticBinding;
  if (staticBinding && registryBinding.module !== staticBinding.module) return staticBinding;
  return registryBinding ?? staticBinding;
}

export function resolvedDynamicRuntimeBindingForCategory(categoryId: string | undefined): DynamicRuntimeBinding | undefined {
  const registryBinding = bindingFromRegistryCategory(categoryId, getCachedRuntimeModuleRegistry());
  return registryBinding ?? dynamicRuntimeBindingForCategory(categoryId);
}

function moduleIdFromRuntimePath(pathname: string): string | undefined {
  const parts = normalizePath(pathname).split('/').filter(Boolean);
  if (parts.length === 0) return undefined;
  return parts[0] === 'pwa' || parts[0] === 'portal' || parts[0] === 'share'
    ? parts[1]
    : parts[0];
}

export function shouldUseDynamicRuntimePage(pathname: string): boolean {
  if (resolvedDynamicRuntimeBindingForPath(pathname)) return true;
  const moduleId = moduleIdFromRuntimePath(pathname);
  return typeof moduleId === 'string' && moduleId.length !== 2;
}

export function isPathInResolvedDynamicRuntimeBinding(pathname: string, binding: DynamicRuntimeBinding): boolean {
  return isPathInDynamicRuntimeBinding(pathname, binding);
}

export function browserRouteForResolvedRuntimeRoute(route: string, binding: DynamicRuntimeBinding | undefined): string {
  return browserRouteForDynamicRuntimeRoute(route, binding);
}

function prettifyScreenKey(screenKey: string): string {
  return screenKey
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function titleForResolvedRuntimePath(
  pathname: string,
  binding: DynamicRuntimeBinding,
  resolvedRoute?: RuntimeResolvedRoute | null,
): string {
  if (resolvedRoute?.screenKey) {
    return prettifyScreenKey(resolvedRoute.screenKey);
  }
  return titleForDynamicRuntimePath(pathname, binding);
}

export async function resolveRuntimeRoute(
  query: RuntimeResolveRouteRequest,
): Promise<RuntimeResolvedRoute> {
  try {
    return await api.get<RuntimeResolvedRoute>('/api/runtime/resolve-route', query);
  } catch {
    const binding = resolvedDynamicRuntimeBindingForPath(query.pathname);
    if (!binding) throw new Error('runtime_route_unresolved');
    const normalized = normalizePath(query.pathname);
    const suffix = normalized.slice(normalizePath(binding.defaultRoute).length).replace(/^\/+/, '');
    const screenKey = suffix.split('/')[0] || 'overview';
    return {
      moduleCode: binding.requiredModule ?? binding.module.toUpperCase(),
      profileCode: binding.profileTemplateCode,
      screenKey,
      screenType: screenKey === 'overview' ? 'overview' : 'entity-list',
      routeBase: binding.defaultRoute,
      canonicalPath: normalized,
      matchedAlias: undefined,
      context: {},
      permissions: {
        canView: true,
        canEdit: true,
        missing: [],
      },
    };
  }
}

export async function getRuntimeScreen(query: ApiQueryParams): Promise<RuntimeScreenResponse> {
  return tryGet<RuntimeScreenResponse>('/api/runtime/screen', '/dynamic-runtime/screen', query);
}

export async function getRuntimeNavigation(query: ApiQueryParams): Promise<RuntimeNavigationGraph> {
  return tryGet<RuntimeNavigationGraph>('/api/runtime/navigation', '/dynamic-runtime/navigation', query);
}

export async function getRuntimeNavigationBadges(query: ApiQueryParams): Promise<{
  graphVersion: string;
  diagnostics: RuntimeNavigationGraph['diagnostics'];
  badges: Record<string, unknown>;
}> {
  return tryGet('/api/runtime/navigation/badges', '/dynamic-runtime/navigation/badges', query);
}

export async function compareRuntimeNavigation(queryString: string, body: unknown): Promise<{
  graphVersion: string;
  diagnostics: RuntimeNavigationGraph['diagnostics'];
  compare: unknown;
}> {
  return tryPost(
    `/api/runtime/navigation/compare?${queryString}`,
    `/dynamic-runtime/navigation/compare?${queryString}`,
    body,
  );
}

export async function recordRuntimeNavigationNodeClick(queryString: string, body: unknown): Promise<void> {
  await tryPost<void>(
    `/api/runtime/navigation/node-click?${queryString}`,
    `/dynamic-runtime/navigation/node-click?${queryString}`,
    body,
  );
}

export async function getRuntimeSettingsSchema(
  query: RuntimeSettingsSchemaRequest,
): Promise<RuntimeSettingsSchemaResponse> {
  return api.get('/api/runtime/settings/schema', query);
}

export async function getRuntimeSettingsData(
  query: RuntimeSettingsSchemaRequest,
): Promise<RuntimeSettingsDataResponse> {
  return api.get('/api/runtime/settings/data', query);
}

export async function dispatchRuntimeActionRequest(body: unknown): Promise<RuntimeActionDispatchResponse> {
  return tryPost('/dynamic-runtime/action/dispatch', '/api/runtime/action/dispatch', body);
}

export async function executeRuntimeActionRequest(body: unknown): Promise<RuntimeActionExecuteResponse> {
  return api.post('/api/v1/dynamic-runtime/action/execute', body);
}

export async function queryRuntimeDataRequest(body: RuntimeDataQueryRequest): Promise<RuntimeDataQueryResponse> {
  return api.post('/api/v1/dynamic-runtime/data/query', body);
}

export async function recordRuntimeActionOutcomeRequest(body: unknown): Promise<RuntimeActionOutcomeResponse> {
  return tryPost('/api/runtime/action/outcome', '/dynamic-runtime/action/outcome', body);
}
